import React, { useRef, useState, useCallback, useImperativeHandle } from "react";
import { PlusIcon } from "components/Icons";
import ImageGallery from "components/ImageGallery";
import "styles/ImageUploadInput.css";

/**
 * ImageUploadInput — multi‑image file picker that re‑uses ImageGallery
 * for display / lightbox. Behaviour remains identical to earlier version.
 *
 * Props:
 *  - onChange?: (files: File[]) => void   // emits the full File[] list
 *  - name?: string                        // form field name, defaults to "images"
 */
export default function ImageUploadInput({ ref, onChange, name = "images" }) {
  const fileInputRef = useRef(null);
  const [images, setImages] = useState([]); // [{ id, file, url }]

  /** Handle <input type="file"> selection */
  const handleFilesSelected = useCallback(
    (evt) => {
      const files = Array.from(evt.target.files || []);
      const newImages = files.map((file) => ({
        id: crypto.randomUUID(),
        file,
        url: URL.createObjectURL(file),
      }));

      setImages((prev) => {
        const updated = [...prev, ...newImages];
        onChange?.(updated.map((img) => img.file));
        return updated;
      });

      // reset value so same file can be chosen again later
      evt.target.value = "";
    },
    [onChange]
  );

  /** Remove an image by id */
  const removeImage = useCallback(
    (id) => {
      setImages((prev) => {
        const updated = prev.filter((img) => img.id !== id);
        onChange?.(updated.map((img) => img.file));
        return updated;
      });
    },
    [onChange]
  );

  const openFileDialog = () => fileInputRef.current?.click();

  useImperativeHandle(ref, () => ({
    clear() { setImages([]); },
  }), []);

  return (
    <div className="iuu-wrapper">
      {/* Hidden native file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        name={name}
        className="iuu-input-file"
        onChange={handleFilesSelected}
      />

      {/* Thumbnails + lightbox */}
      <ImageGallery images={images} onDelete={removeImage} />

      {/* “+” button */}
      <button
        type="button"
        aria-label="Add images"
        className="iuu-add-btn"
        onClick={openFileDialog}
      >
        <PlusIcon width={20} height={20} />
      </button>
    </div>
  );
}
